"use client";

/**
 * "My Map" editor band shown inside the listing detail drawer for
 * signed-in users. Save the listing to your map, label it with a
 * viewing status, and jot a short private note. Used identically from
 * the all-listings map layer, the listings grid, and the My-Map layer
 * (single editor, one source of truth via useMyPins).
 *
 * Dark-mode discipline: bg-white surface (auto-flips), base text-gray-*,
 * explicit-hex status swatches (the palette remap can't dim them).
 */
import { useEffect, useRef, useState } from "react";
import Link from "@/lib/localized-link";
import { Heart, MapPinned, ArrowRight } from "lucide-react";
import { useT } from "@/lib/locale-context";
import { haptic } from "@/lib/haptics";
import { useMyPins } from "@/lib/use-my-pins";
import {
  PIN_STATUS_ORDER, STATUS_COLOR, STATUS_LABEL_KEY, NOTES_MAX_LEN,
  type PinStatus,
} from "@/lib/pin-status";

/**
 * Saving itself happens from the heart in the panel's top bar (owner,
 * 2026-09-19: the full-width "save" button pinned above the actions
 * read as pushy). This band therefore renders ONLY once the listing is
 * saved, as the place to label and annotate it.
 */
export default function MyMapEditor({
  listingId,
  onMyMapLayer = false,
}: {
  listingId: string | null;
  /** True when opened from the My Map (personal) map layer: the pin is
   *  already in the map, so we drop the redundant "view in My Map" link
   *  and give the band a distinct accent so it reads as "your pin". */
  onMyMapLayer?: boolean;
}) {
  const t = useT();
  const { signedIn, getPin, remove, patch } = useMyPins();
  const pin = getPin(listingId);
  const saved = !!pin;

  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [notesFocus, setNotesFocus] = useState(false);
  const lastSavedNotes = useRef("");

  // Sync the notes field whenever the listing (or its saved note) changes.
  useEffect(() => {
    const v = pin?.notes ?? "";
    setNotes(v);
    lastSavedNotes.current = v;
  }, [listingId, pin?.notes]);

  if (!signedIn || !listingId || !saved) return null;

  async function onRemove() {
    if (busy) return;
    setBusy(true);
    try {
      await remove(listingId!);
    } finally {
      setBusy(false);
    }
  }

  async function onStatus(next: PinStatus) {
    haptic(6);
    // Clicking the active status clears it (back to plain "saved").
    const value = pin?.status === next ? null : next;
    try {
      await patch(listingId!, { status: value });
    } catch {
      /* keep the previous status on failure */
    }
  }

  function onNotesBlur() {
    const v = notes.trim().slice(0, NOTES_MAX_LEN);
    if (v === lastSavedNotes.current) return;
    lastSavedNotes.current = v;
    void patch(listingId!, { notes: v }).catch(() => {});
  }

  return (
    <div
      data-sheet-no-drag
      className={`shrink-0 border-t px-4 py-2.5 ${
        onMyMapLayer
          ? "border-gray-200 bg-gray-50"
          : "border-gray-100 bg-white"
      }`}
    >
      <div className="flex items-center gap-3">
        <span className="inline-flex min-w-0 items-center gap-1.5 text-xs font-semibold text-gray-700">
          <Heart className="h-3.5 w-3.5 shrink-0 fill-rose-500 text-rose-500" />
          <span className="truncate">{t("mymap.saved")}</span>
        </span>
        {!onMyMapLayer && (
          <Link
            href="/map/personal"
            className="inline-flex items-center gap-1 text-xs font-semibold text-gray-900 underline-offset-2 underline decoration-gray-300 hover:decoration-gray-900"
          >
            <MapPinned className="h-3.5 w-3.5" />
            {t("mymap.view")}
            <ArrowRight className="h-3 w-3" />
          </Link>
        )}
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          className="ml-auto text-xs font-medium text-gray-400 hover:text-rose-600 disabled:opacity-60"
        >
          {t("mymap.remove")}
        </button>
      </div>

      {/* Status chips - one scrolling row, explicit hex swatch + fill on
          the active one. */}
      <div className="no-scrollbar -mx-4 mt-2 flex gap-1.5 overflow-x-auto px-4">
        {PIN_STATUS_ORDER.map((s) => {
          const activeStatus = pin?.status === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => onStatus(s)}
              aria-pressed={activeStatus}
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                activeStatus
                  ? "border-transparent text-white"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-white/15 dark:hover:bg-white/5"
              }`}
              style={activeStatus ? { backgroundColor: STATUS_COLOR[s] } : undefined}
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: activeStatus ? "#ffffff" : STATUS_COLOR[s] }}
              />
              {t(STATUS_LABEL_KEY[s])}
            </button>
          );
        })}
      </div>

      {/* Private note: one line at rest, grows while editing. */}
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value.slice(0, NOTES_MAX_LEN))}
        onFocus={() => setNotesFocus(true)}
        onBlur={() => { setNotesFocus(false); onNotesBlur(); }}
        maxLength={NOTES_MAX_LEN}
        rows={notesFocus || notes.length > 60 ? 3 : 1}
        placeholder={t("mymap.notes.placeholder")}
        className="mt-2 w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 dark:border-white/15"
      />
      {notesFocus && (
        <div className="mt-0.5 text-right text-[11px] tabular-nums text-gray-400">
          {notes.length}/{NOTES_MAX_LEN}
        </div>
      )}
    </div>
  );
}
